import { ImmunizationSearch, SearchStatus } from "../../models/Interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import "./searchCard.css";
import getIzProvider from "../../models/providers/IzProviders";
import { refreshingRecordsAtom, enqueuedIzSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { getDateTime, getWaitTimeText } from "../../utils/data";

interface SearchCardProps {
  index: number;
  search: ImmunizationSearch;
  onSelect: Function;
}

type StatusColor = "red" | "green" | "yellow" | "purple";

export default function SearchCard({ search, index, onSelect }: SearchCardProps) {
  const { t } = useTranslation();
  const dateVerified = getDateTime(search.dateVerified).isValid;
  const [refreshingRecords] = useAtom(refreshingRecordsAtom);
  const [enqueuedIzSearch] = useAtom(enqueuedIzSearchAtom);
  const izProvider = getIzProvider(search.izProviderKey, t);
  const isVerifiedSearch = () => {
    return (
      search.status.trim() !== SearchStatus.noMatch &&
      search.status.trim() !== SearchStatus.basicMatchNoContacts &&
      search.status.trim() !== SearchStatus.partialMatchNoContacts &&
      dateVerified
    );
  };

  const recordsLoading = refreshingRecords.find(
    (record) => record.attributes.iz_search_uid === search.uid
  );

  const getText = () => {
    const searchStatusToLabel = new Map<string, string>([
      [SearchStatus.inQueue, t("immunizations.search_results_pending")],
      [SearchStatus.match, t("immunizations.search_records_found")],
      [SearchStatus.noMatch, t("immunizations.search_try_again")],
      [SearchStatus.noRecords, t("immunizations.search_try_again")],
      [SearchStatus.multiMatch, t("immunizations.search_results_multi_match")],
      [SearchStatus.partialMatchNoContacts, t("immunizations.search_try_again")],
      [SearchStatus.basicMatchNoContacts, t("immunizations.search_basic_match_no_contacts")],
    ]);

    if (!search.status) {
      return searchStatusToLabel.get(SearchStatus.inQueue);
    }

    const searchStatus = search.status.trim();
    if (searchStatus === SearchStatus.partialMatchAltContacts && dateVerified) {
      // This is effectively a MATCH
      return searchStatusToLabel.get(SearchStatus.match);
    }

    if (searchStatusToLabel.has(searchStatus)) {
      return searchStatusToLabel.get(searchStatus);
    }

    // Un-verified PARTIAL_MATCH_ALT_CONTACTS
    return t("immunizations.search_search_incomplete");
  };

  const getColor = (): StatusColor => {
    let s: StatusColor = "red";
    if (
      search.status.trim() === SearchStatus.noMatch ||
      search.status.trim() === SearchStatus.noRecords ||
      search.status.trim() === SearchStatus.multiMatch ||
      search.status.trim() === SearchStatus.basicMatchNoContacts
    ) {
      s = "red";
    } else if (search.status.trim() !== SearchStatus.noMatch && dateVerified) {
      s = "green";
    } else if (search.status.trim() === SearchStatus.partialMatchAltContacts) {
      s = "yellow";
    } else if (
      (search.status.trim() !== SearchStatus.match && !dateVerified) ||
      (search.status.trim() === SearchStatus.match && recordsLoading)
    ) {
      s = "purple";
    } else {
      if (search.status.trim() === SearchStatus.partialMatchNoContacts) {
        s = "red";
      } else {
        s = "yellow";
      }
    }
    return s;
  };

  const getColorSrc = () => {
    const color = getColor();
    switch (color) {
      case "red":
        return "/assets/images/red_status.png";
      case "green":
        return "/assets/images/green_status.png";
      case "yellow":
        return "/assets/images/yellow_status.png";
      case "purple":
        return "/assets/images/clock_regular.svg";
      default:
        return ""; // fallback
    }
  };

  const getClassName = () => {
    return search.status.trim() === SearchStatus.inQueue ? "search-card in-queue" : "search-card";
  };

  const name = search.childFirstName
    ? `${search.childFirstName} ${search.childLastName}`
    : `${search.firstName} ${search.lastName}`;

  return (
    <>
      <div
        data-testid="desktop-container"
        className={`max-md:hidden ${getClassName()} ${getColor()} rounded-tl-lg rounded-bl-lg`}
        onClick={() => onSelect()}
      >
        <div className={`badge ${getColor()}`}></div>
        <div className="flex flex-col gap-1 py-4 text-black">
          <p className="text-lg font-black">{getText() + "  "}</p>
          <p data-testid="search-card-name-desktop">{name}</p>
          <p className="text-lg font-black">
            {`${izProvider?.jurisdictionAbbrev}: ${t("immunizations.search_updated")} ${getDateTime(
              search.dateUpdated
            ).toLocaleString()}`}
          </p>
        </div>
      </div>
      <div
        data-testid="mobile-container"
        className={`md:hidden rounded-lg shadow-md flex p-2 mobile-card ${
          search.status.trim() == SearchStatus.inQueue ? "bg-primary-light-blue" : "bg-white"
        }`}
        onClick={() => onSelect()}
      >
        <div className="flex flex-col gap-0.5 text-black">
          <div className="flex gap-1">
            <p className="text-lg font-semibold my-auto">{getText() + "  "}</p>
            <img src={getColorSrc()} className="w-5 h-5 mb-[5px] mt-[3px]"></img>
            {enqueuedIzSearch?.estimated_wait_time && getColor() === "purple" && (
              <p className="text-sm my-auto">
                {getWaitTimeText(enqueuedIzSearch?.estimated_wait_time)}
              </p>
            )}
          </div>
          <p data-testid="search-card-name-mobile" className="font-semibold">
            {name}
          </p>
          <p className="text-lg">
            <strong className="">{izProvider?.jurisdictionAbbrev}</strong>
            {`: ${t("immunizations.search_updated")} ${getDateTime(
              search.dateUpdated
            ).toLocaleString()}`}
          </p>
        </div>
        <img src="/assets/images/chevron_right.svg" className="h-6 w-6 m-auto mr-0" />
      </div>
    </>
  );
}
